<script setup>
import { reactive, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { isPhoneE164 } from 'shared/helpers/Validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageHeader from '../../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

// Canonical slugs live in state; UI labels come from i18n keys only so
// renames stay in the translation file, not in component logic.
const provider = ref('twilio');
const turnProvider = ref('cloudflare');

const twilio = reactive({
  phoneNumber: '',
  accountSid: '',
  authToken: '',
  apiKeySid: '',
  apiKeySecret: '',
});

const webrtcName = ref('');
const coturn = reactive({
  turnUrls: '',
  turnRealm: '',
  turnSharedSecret: '',
});
const cloudflare = reactive({
  appId: '',
  appSecret: '',
});
const custom = reactive({
  turnUrls: '',
  turnUsername: '',
  turnCredential: '',
});

const uiFlags = useMapGetter('inboxes/getUIFlags');

const isTwilio = computed(() => provider.value === 'twilio');
const isWebrtc = computed(() => provider.value === 'webrtc');

// Conditional validation — only the rules for the currently selected
// provider/turnProvider apply. Unvisited branches are unrestricted.
const rules = computed(() => {
  if (isTwilio.value) {
    return {
      twilio: {
        phoneNumber: { required, isPhoneE164 },
        accountSid: { required },
        authToken: { required },
        apiKeySid: { required },
        apiKeySecret: { required },
      },
    };
  }

  const webrtcRules = {
    webrtcName: { required },
  };

  if (turnProvider.value === 'coturn') {
    webrtcRules.coturn = {
      turnUrls: { required },
      turnRealm: { required },
      turnSharedSecret: { required },
    };
  } else if (turnProvider.value === 'cloudflare') {
    webrtcRules.cloudflare = {
      appId: { required },
      appSecret: { required },
    };
  } else {
    webrtcRules.custom = {
      turnUrls: { required },
      turnUsername: { required },
      turnCredential: { required },
    };
  }

  return webrtcRules;
});

const state = computed(() => {
  if (isTwilio.value) return { twilio };
  return { webrtcName: webrtcName.value, coturn, cloudflare, custom };
});

const v$ = useVuelidate(rules, state);
const isSubmitDisabled = computed(() => v$.value.$invalid);

const parseTurnUrls = raw =>
  raw
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

const buildTwilioConfig = () => ({
  name: `Voice (${twilio.phoneNumber})`,
  voice: {
    phone_number: twilio.phoneNumber,
    provider: 'twilio',
    provider_config: {
      account_sid: twilio.accountSid,
      auth_token: twilio.authToken,
      api_key_sid: twilio.apiKeySid,
      api_key_secret: twilio.apiKeySecret,
    },
  },
});

const buildWebrtcConfig = () => {
  let providerConfig;
  if (turnProvider.value === 'coturn') {
    providerConfig = {
      turn_provider: 'coturn',
      turn_urls: parseTurnUrls(coturn.turnUrls),
      turn_realm: coturn.turnRealm,
      turn_shared_secret: coturn.turnSharedSecret,
    };
  } else if (turnProvider.value === 'cloudflare') {
    providerConfig = {
      turn_provider: 'cloudflare',
      cloudflare_turn_app_id: cloudflare.appId,
      cloudflare_turn_app_secret: cloudflare.appSecret,
    };
  } else {
    providerConfig = {
      turn_provider: 'custom',
      turn_urls: parseTurnUrls(custom.turnUrls),
      turn_username: custom.turnUsername,
      turn_credential: custom.turnCredential,
    };
  }

  return {
    name: webrtcName.value,
    voice: {
      provider: 'webrtc',
      provider_config: providerConfig,
    },
  };
};

async function createChannel() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    const payload = isWebrtc.value ? buildWebrtcConfig() : buildTwilioConfig();
    const channel = await store.dispatch('inboxes/createVoiceChannel', payload);

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: channel.id },
    });
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('INBOX_MGMT.ADD.VOICE.API.ERROR_MESSAGE')
    );
  }
}
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.VOICE.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.VOICE.DESC')"
    />

    <div class="flex flex-col gap-2 my-4">
      <label class="text-sm font-medium">
        {{ t('INBOX_MGMT.ADD.VOICE.PROVIDER_TOGGLE.LABEL') }}
      </label>
      <div class="flex gap-2">
        <NextButton
          type="button"
          size="sm"
          :variant="isTwilio ? 'solid' : 'faded'"
          :label="t('INBOX_MGMT.ADD.VOICE.PROVIDER_TOGGLE.TWILIO')"
          @click="provider = 'twilio'"
        />
        <NextButton
          type="button"
          size="sm"
          :variant="isWebrtc ? 'solid' : 'faded'"
          :label="t('INBOX_MGMT.ADD.VOICE.PROVIDER_TOGGLE.WEBRTC')"
          @click="provider = 'webrtc'"
        />
      </div>
    </div>

    <form
      class="flex flex-col gap-4 flex-wrap mx-0"
      @submit.prevent="createChannel"
    >
      <template v-if="isTwilio">
        <Input
          v-model="twilio.phoneNumber"
          :label="t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.PLACEHOLDER')"
          :message="
            v$.twilio?.phoneNumber?.$error
              ? t('INBOX_MGMT.ADD.VOICE.PHONE_NUMBER.ERROR')
              : ''
          "
          :message-type="v$.twilio?.phoneNumber?.$error ? 'error' : 'info'"
          @blur="v$.twilio?.phoneNumber?.$touch"
        />
        <Input
          v-model="twilio.accountSid"
          :label="t('INBOX_MGMT.ADD.VOICE.TWILIO.ACCOUNT_SID.LABEL')"
          :placeholder="
            t('INBOX_MGMT.ADD.VOICE.TWILIO.ACCOUNT_SID.PLACEHOLDER')
          "
          :message="
            v$.twilio?.accountSid?.$error
              ? t('INBOX_MGMT.ADD.VOICE.TWILIO.ACCOUNT_SID.REQUIRED')
              : ''
          "
          :message-type="v$.twilio?.accountSid?.$error ? 'error' : 'info'"
          @blur="v$.twilio?.accountSid?.$touch"
        />
        <Input
          v-model="twilio.authToken"
          type="password"
          :label="t('INBOX_MGMT.ADD.VOICE.TWILIO.AUTH_TOKEN.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VOICE.TWILIO.AUTH_TOKEN.PLACEHOLDER')"
          :message="
            v$.twilio?.authToken?.$error
              ? t('INBOX_MGMT.ADD.VOICE.TWILIO.AUTH_TOKEN.REQUIRED')
              : ''
          "
          :message-type="v$.twilio?.authToken?.$error ? 'error' : 'info'"
          @blur="v$.twilio?.authToken?.$touch"
        />
        <Input
          v-model="twilio.apiKeySid"
          :label="t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.LABEL')"
          :placeholder="
            t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.PLACEHOLDER')
          "
          :message="
            v$.twilio?.apiKeySid?.$error
              ? t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.REQUIRED')
              : ''
          "
          :message-type="v$.twilio?.apiKeySid?.$error ? 'error' : 'info'"
          @blur="v$.twilio?.apiKeySid?.$touch"
        />
        <Input
          v-model="twilio.apiKeySecret"
          type="password"
          :label="t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.LABEL')"
          :placeholder="
            t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.PLACEHOLDER')
          "
          :message="
            v$.twilio?.apiKeySecret?.$error
              ? t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.REQUIRED')
              : ''
          "
          :message-type="v$.twilio?.apiKeySecret?.$error ? 'error' : 'info'"
          @blur="v$.twilio?.apiKeySecret?.$touch"
        />
      </template>

      <template v-else>
        <p class="text-sm text-slate-600 dark:text-slate-400">
          {{ t('INBOX_MGMT.ADD.VOICE.WEBRTC.INTRO') }}
        </p>

        <Input
          v-model="webrtcName"
          :label="t('INBOX_MGMT.ADD.VOICE.WEBRTC.INBOX_NAME.LABEL')"
          :placeholder="t('INBOX_MGMT.ADD.VOICE.WEBRTC.INBOX_NAME.PLACEHOLDER')"
          :message="
            v$.webrtcName?.$error
              ? t('INBOX_MGMT.ADD.VOICE.WEBRTC.INBOX_NAME.REQUIRED')
              : ''
          "
          :message-type="v$.webrtcName?.$error ? 'error' : 'info'"
          @blur="v$.webrtcName?.$touch"
        />

        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium">
            {{ t('INBOX_MGMT.ADD.VOICE.WEBRTC.TURN_PROVIDER.LABEL') }}
          </label>
          <select
            v-model="turnProvider"
            class="rounded border border-slate-200 px-3 py-2 text-sm dark:bg-slate-800 dark:border-slate-700"
          >
            <option value="cloudflare">
              {{ t('INBOX_MGMT.ADD.VOICE.WEBRTC.TURN_PROVIDER.CLOUDFLARE') }}
            </option>
            <option value="coturn">
              {{ t('INBOX_MGMT.ADD.VOICE.WEBRTC.TURN_PROVIDER.COTURN') }}
            </option>
            <option value="custom">
              {{ t('INBOX_MGMT.ADD.VOICE.WEBRTC.TURN_PROVIDER.CUSTOM') }}
            </option>
          </select>
          <p class="text-xs text-slate-500 dark:text-slate-400">
            {{ t('INBOX_MGMT.ADD.VOICE.WEBRTC.TURN_PROVIDER.HELP') }}
          </p>
        </div>

        <template v-if="turnProvider === 'coturn'">
          <label class="text-sm font-medium">
            {{ t('INBOX_MGMT.ADD.VOICE.WEBRTC.COTURN.TURN_URLS.LABEL') }}
          </label>
          <textarea
            v-model="coturn.turnUrls"
            class="rounded border border-slate-200 px-3 py-2 text-sm min-h-[96px] dark:bg-slate-800 dark:border-slate-700"
            :placeholder="
              t('INBOX_MGMT.ADD.VOICE.WEBRTC.COTURN.TURN_URLS.PLACEHOLDER')
            "
            @blur="v$.coturn?.turnUrls?.$touch"
          />
          <span
            v-if="v$.coturn?.turnUrls?.$error"
            class="text-xs text-ruby-600"
          >
            {{ t('INBOX_MGMT.ADD.VOICE.WEBRTC.COTURN.TURN_URLS.REQUIRED') }}
          </span>
          <Input
            v-model="coturn.turnRealm"
            :label="t('INBOX_MGMT.ADD.VOICE.WEBRTC.COTURN.TURN_REALM.LABEL')"
            :placeholder="
              t('INBOX_MGMT.ADD.VOICE.WEBRTC.COTURN.TURN_REALM.PLACEHOLDER')
            "
            :message="
              v$.coturn?.turnRealm?.$error
                ? t('INBOX_MGMT.ADD.VOICE.WEBRTC.COTURN.TURN_REALM.REQUIRED')
                : ''
            "
            :message-type="v$.coturn?.turnRealm?.$error ? 'error' : 'info'"
            @blur="v$.coturn?.turnRealm?.$touch"
          />
          <Input
            v-model="coturn.turnSharedSecret"
            type="password"
            :label="
              t('INBOX_MGMT.ADD.VOICE.WEBRTC.COTURN.TURN_SHARED_SECRET.LABEL')
            "
            :placeholder="
              t(
                'INBOX_MGMT.ADD.VOICE.WEBRTC.COTURN.TURN_SHARED_SECRET.PLACEHOLDER'
              )
            "
            :message="
              v$.coturn?.turnSharedSecret?.$error
                ? t(
                    'INBOX_MGMT.ADD.VOICE.WEBRTC.COTURN.TURN_SHARED_SECRET.REQUIRED'
                  )
                : ''
            "
            :message-type="
              v$.coturn?.turnSharedSecret?.$error ? 'error' : 'info'
            "
            @blur="v$.coturn?.turnSharedSecret?.$touch"
          />
        </template>

        <template v-else-if="turnProvider === 'cloudflare'">
          <Input
            v-model="cloudflare.appId"
            :label="t('INBOX_MGMT.ADD.VOICE.WEBRTC.CLOUDFLARE.APP_ID.LABEL')"
            :placeholder="
              t('INBOX_MGMT.ADD.VOICE.WEBRTC.CLOUDFLARE.APP_ID.PLACEHOLDER')
            "
            :message="
              v$.cloudflare?.appId?.$error
                ? t('INBOX_MGMT.ADD.VOICE.WEBRTC.CLOUDFLARE.APP_ID.REQUIRED')
                : ''
            "
            :message-type="v$.cloudflare?.appId?.$error ? 'error' : 'info'"
            @blur="v$.cloudflare?.appId?.$touch"
          />
          <Input
            v-model="cloudflare.appSecret"
            type="password"
            :label="
              t('INBOX_MGMT.ADD.VOICE.WEBRTC.CLOUDFLARE.APP_SECRET.LABEL')
            "
            :placeholder="
              t('INBOX_MGMT.ADD.VOICE.WEBRTC.CLOUDFLARE.APP_SECRET.PLACEHOLDER')
            "
            :message="
              v$.cloudflare?.appSecret?.$error
                ? t(
                    'INBOX_MGMT.ADD.VOICE.WEBRTC.CLOUDFLARE.APP_SECRET.REQUIRED'
                  )
                : ''
            "
            :message-type="v$.cloudflare?.appSecret?.$error ? 'error' : 'info'"
            @blur="v$.cloudflare?.appSecret?.$touch"
          />
        </template>

        <template v-else>
          <label class="text-sm font-medium">
            {{ t('INBOX_MGMT.ADD.VOICE.WEBRTC.CUSTOM.TURN_URLS.LABEL') }}
          </label>
          <textarea
            v-model="custom.turnUrls"
            class="rounded border border-slate-200 px-3 py-2 text-sm min-h-[96px] dark:bg-slate-800 dark:border-slate-700"
            :placeholder="
              t('INBOX_MGMT.ADD.VOICE.WEBRTC.CUSTOM.TURN_URLS.PLACEHOLDER')
            "
            @blur="v$.custom?.turnUrls?.$touch"
          />
          <span
            v-if="v$.custom?.turnUrls?.$error"
            class="text-xs text-ruby-600"
          >
            {{ t('INBOX_MGMT.ADD.VOICE.WEBRTC.CUSTOM.TURN_URLS.REQUIRED') }}
          </span>
          <Input
            v-model="custom.turnUsername"
            :label="t('INBOX_MGMT.ADD.VOICE.WEBRTC.CUSTOM.TURN_USERNAME.LABEL')"
            :placeholder="
              t('INBOX_MGMT.ADD.VOICE.WEBRTC.CUSTOM.TURN_USERNAME.PLACEHOLDER')
            "
            :message="
              v$.custom?.turnUsername?.$error
                ? t('INBOX_MGMT.ADD.VOICE.WEBRTC.CUSTOM.TURN_USERNAME.REQUIRED')
                : ''
            "
            :message-type="v$.custom?.turnUsername?.$error ? 'error' : 'info'"
            @blur="v$.custom?.turnUsername?.$touch"
          />
          <Input
            v-model="custom.turnCredential"
            type="password"
            :label="
              t('INBOX_MGMT.ADD.VOICE.WEBRTC.CUSTOM.TURN_CREDENTIAL.LABEL')
            "
            :placeholder="
              t(
                'INBOX_MGMT.ADD.VOICE.WEBRTC.CUSTOM.TURN_CREDENTIAL.PLACEHOLDER'
              )
            "
            :message="
              v$.custom?.turnCredential?.$error
                ? t(
                    'INBOX_MGMT.ADD.VOICE.WEBRTC.CUSTOM.TURN_CREDENTIAL.REQUIRED'
                  )
                : ''
            "
            :message-type="v$.custom?.turnCredential?.$error ? 'error' : 'info'"
            @blur="v$.custom?.turnCredential?.$touch"
          />
        </template>
      </template>

      <div>
        <NextButton
          :is-loading="uiFlags.isCreating"
          :disabled="isSubmitDisabled"
          :label="t('INBOX_MGMT.ADD.VOICE.SUBMIT_BUTTON')"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>
